import { useState } from "react";
import type { Contrato } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { usePlanos, useTrocarPlano, type EscopoDeContrato } from "@/api/consultas";
import { textoDoErro } from "@/api/problema";
import { Campo } from "@/componentes/Campo";
import { Selecao } from "@/componentes/Selecao";
import { ModalDeCadastro } from "@/componentes/ModalDeCadastro";
import { useAvisos } from "@/componentes/Avisos";
import { Dica } from "@/componentes/Dica";
import { ehDiaPrimeiro, primeiroDoProximoMes, vespera } from "@/calendario";
import { dia } from "@/formato";
import { campoDoContrato, opcoesDePlano } from "./NovoContrato";

/*
 * Troca de plano: o vigente termina na véspera do início, e o novo abre nele.
 *
 * A data sugerida é o dia 1º do mês seguinte, porque é a troca que não mexe na
 * competência em andamento. Fora do dia 1º a troca é permitida, mas a quota é
 * por competência e por aplicação, e não por contrato: o consumo do plano
 * antigo no mês já conta na franquia do novo, e a mensalidade do novo é
 * proporcional aos dias. Não há rateio da franquia em lugar nenhum — nem na
 * quota nem na fatura (design da add-planos-e-contratos-no-painel, "Notas da
 * implementação"). A tela diz isso quando a data sai do dia 1º, não antes: a
 * frase curta na tela e o efeito em dinheiro na Dica.
 */
const EFEITO_FORA_DO_DIA_1 =
  "A quota é por mês: as emissões do plano atual neste mês consomem a franquia do novo, " +
  "e a mensalidade do novo é proporcional aos dias. Com franquia menor, o resto do mês " +
  "tende a sair como excedente; com franquia maior, o novo ganha a franquia inteira.";
export function TrocarPlano({
  escopo,
  nome,
  vigente,
  aoFechar,
}: {
  escopo: EscopoDeContrato;
  nome: string;
  vigente: Contrato;
  aoFechar: () => void;
}) {
  const { cliente } = useSessao();
  const avisos = useAvisos();
  const { data } = usePlanos(cliente, true);
  const trocar = useTrocarPlano(cliente, escopo);

  const [planoID, setPlanoID] = useState("");
  const [inicio, setInicio] = useState(() => primeiroDoProximoMes());
  const [erros, setErros] = useState<Partial<Record<"plano_id" | "inicio" | "fim", string>>>({});
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);

  function enviar() {
    setErroGeral(undefined);
    if (planoID === "") {
      setErros({ plano_id: "Escolha o plano." });
      return;
    }
    setErros({});

    trocar.mutate(
      { plano_id: planoID, inicio },
      {
        onSuccess: () => {
          avisos.feito("Plano trocado");
          aoFechar();
        },
        onError: (falha) => {
          const campo = campoDoContrato(falha);
          if (campo !== null) setErros({ [campo]: textoDoErro(falha) });
          else setErroGeral(textoDoErro(falha));
        },
      },
    );
  }

  return (
    <ModalDeCadastro
      aberto
      aoFechar={aoFechar}
      titulo="Trocar de plano"
      descricao={`${nome} · ${vigente.plano_nome}`}
      aoEnviar={enviar}
      enviando={trocar.isPending}
      erro={erroGeral}
      rotuloDeEnvio="Trocar plano"
    >
      <Selecao
        rotulo="Plano novo"
        opcoes={opcoesDePlano(data?.planos ?? [], vigente.plano_id)}
        value={planoID}
        onChange={(evento) => setPlanoID(evento.target.value)}
        erro={erros.plano_id}
        autoFocus
      />

      <Campo
        rotulo="Início"
        type="date"
        required
        className="w-44"
        value={inicio}
        onChange={(evento) => setInicio(evento.target.value)}
        erro={erros.inicio}
      />

      {inicio !== "" && (
        <p className="dados text-[12px] text-texto-2">
          {vigente.plano_nome} até {dia(vespera(inicio))}
        </p>
      )}

      {inicio !== "" && !ehDiaPrimeiro(inicio) && (
        <p role="status" className="flex items-center gap-1 text-[12px] text-transito">
          Fora do dia 1º: o consumo do mês conta na franquia nova.
          <Dica texto={EFEITO_FORA_DO_DIA_1} />
        </p>
      )}
    </ModalDeCadastro>
  );
}
